import { useMemo, useState, type ReactNode } from "react";
import { useStockItems } from "../../../hooks/useStockItems";
import { useAnimals } from "../../../hooks/useAnimals";
import { usePayments } from "../../../hooks/usePayments";
import { exportRowsToCsv, type ExportColumn } from "../../../lib/excelExport";
import type { StockItem } from "../../../lib/stockStore";

function fmt(value: number) {
  return value.toLocaleString("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 });
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function num(value: string) {
  return Number(String(value).replace(/\./g, "").replace(",", ".")) || 0;
}

function stockCost(item: StockItem) {
  return Number(item.alisTutari || 0)
    + Number(item.nakliyeMaliyeti || 0)
    + Number(item.kesimMaliyeti || 0)
    + Number(item.parcalamaMaliyeti || 0)
    + Number(item.maliyetTutari || 0);
}

function Label({ children }: { children: ReactNode }) {
  return <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-farm-muted">{children}</label>;
}

function Stat({ label, value, tone = "brown" }: { label: string; value: string | number; tone?: "brown" | "green" | "earth" | "red" | "gold" }) {
  const cls = {
    brown: "text-farm-brown",
    green: "text-farm-green",
    earth: "text-farm-earth",
    red: "text-red-600",
    gold: "text-farm-gold",
  }[tone];
  return (
    <div className="rounded-farm border border-farm-border bg-white px-3 py-2.5">
      <p className="text-[11px] text-farm-muted">{label}</p>
      <p className={`mt-0.5 text-sm font-bold ${cls}`}>{value}</p>
    </div>
  );
}

export default function StokPage() {
  const { items: stockItems, setAllStockItems } = useStockItems();
  const { animals } = useAnimals();
  const { payments } = usePayments();

  const [form, setForm] = useState({
    tarih: today(),
    kupeNo: "",
    siparisNo: "",
    tur: "Büyükbaş" as StockItem["tur"],
    kategori: "Canlı Hayvan",
    tedarikci: "",
    belgeNo: "",
    adet: "1",
    kg: "",
    kesimKg: "",
    alisTutari: "",
    nakliyeMaliyeti: "",
    kesimMaliyeti: "",
    parcalamaMaliyeti: "",
    maliyetTutari: "",
    satisGeliri: "",
    durum: "Stokta" as StockItem["durum"],
    not: "",
  });
  const [query, setQuery] = useState("");
  const [durumFilter, setDurumFilter] = useState("Tümü");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [bulkText, setBulkText] = useState("");

  const enriched = useMemo(() => {
    return stockItems.map((item) => {
      const animal = animals.find((a) => a.kupeNo === item.kupeNo || String(a.siraNo) === item.siparisNo);
      const animalPayments = payments.filter((payment) => payment.hayvanKupeNo === item.kupeNo || payment.animalId === animal?.id);
      const systemRevenue = animal?.hisseler.reduce((sum, share) => sum + share.tutar + (share.kesimUcreti ?? 0) + (share.parcalamaUcreti ?? 0) + (share.tasimaUcreti ?? 0) - (share.indirim ?? 0), 0) ?? 0;
      const paid = animalPayments.reduce((sum, payment) => sum + payment.tutar, 0);
      const revenue = Number(item.satisGeliri || 0) || systemRevenue;
      const cost = stockCost(item);
      return {
        ...item,
        matchedAnimalId: animal?.id,
        systemRevenue,
        paid,
        revenue,
        cost,
        profit: revenue - cost,
      };
    });
  }, [stockItems, animals, payments]);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr-TR");
    return enriched.filter((item) => {
      if (durumFilter !== "Tümü" && item.durum !== durumFilter) return false;
      if (!q) return true;
      return `${item.kupeNo} ${item.siparisNo ?? ""} ${item.tur} ${item.kategori ?? ""} ${item.tedarikci ?? ""} ${item.not ?? ""}`
        .toLocaleLowerCase("tr-TR")
        .includes(q);
    });
  }, [query, enriched, durumFilter]);

  const totals = filtered.reduce((acc, item) => ({
    count: acc.count + 1,
    kg: acc.kg + Number(item.kg || 0),
    cost: acc.cost + item.cost,
    revenue: acc.revenue + item.revenue,
    profit: acc.profit + item.profit,
    paid: acc.paid + item.paid,
  }), { count: 0, kg: 0, cost: 0, revenue: 0, profit: 0, paid: 0 });

  function resetForm() {
    setEditingId(null);
    setForm({
      tarih: today(),
      kupeNo: "",
      siparisNo: "",
      tur: "Büyükbaş",
      kategori: "Canlı Hayvan",
      tedarikci: "",
      belgeNo: "",
      adet: "1",
      kg: "",
      kesimKg: "",
      alisTutari: "",
      nakliyeMaliyeti: "",
      kesimMaliyeti: "",
      parcalamaMaliyeti: "",
      maliyetTutari: "",
      satisGeliri: "",
      durum: "Stokta",
      not: "",
    });
  }

  function editItem(item: StockItem) {
    setEditingId(item.id);
    setForm({
      tarih: item.tarih,
      kupeNo: item.kupeNo,
      siparisNo: item.siparisNo ?? "",
      tur: item.tur,
      kategori: item.kategori ?? "Canlı Hayvan",
      tedarikci: item.tedarikci ?? "",
      belgeNo: item.belgeNo ?? "",
      adet: String(item.adet ?? 1),
      kg: String(item.kg ?? ""),
      kesimKg: String(item.kesimKg ?? ""),
      alisTutari: String(item.alisTutari ?? ""),
      nakliyeMaliyeti: String(item.nakliyeMaliyeti ?? ""),
      kesimMaliyeti: String(item.kesimMaliyeti ?? ""),
      parcalamaMaliyeti: String(item.parcalamaMaliyeti ?? ""),
      maliyetTutari: String(item.maliyetTutari ?? ""),
      satisGeliri: String(item.satisGeliri ?? ""),
      durum: item.durum,
      not: item.not ?? "",
    });
  }

  async function handleSave() {
    if (!form.kupeNo.trim()) {
      window.alert("Küpe no / stok kodu zorunludur.");
      return;
    }

    const now = new Date().toISOString();
    const next: StockItem = {
      id: editingId ?? crypto.randomUUID(),
      tarih: form.tarih || today(),
      kupeNo: form.kupeNo.trim(),
      siparisNo: form.siparisNo.trim() || undefined,
      tur: form.tur,
      kategori: form.kategori.trim() || undefined,
      tedarikci: form.tedarikci.trim() || undefined,
      belgeNo: form.belgeNo.trim() || undefined,
      adet: num(form.adet) || 1,
      kg: num(form.kg),
      kesimKg: num(form.kesimKg),
      alisTutari: num(form.alisTutari),
      nakliyeMaliyeti: num(form.nakliyeMaliyeti),
      kesimMaliyeti: num(form.kesimMaliyeti),
      parcalamaMaliyeti: num(form.parcalamaMaliyeti),
      maliyetTutari: num(form.maliyetTutari),
      satisGeliri: num(form.satisGeliri),
      not: form.not.trim(),
      durum: form.durum,
      createdAt: stockItems.find((item) => item.id === editingId)?.createdAt ?? now,
      updatedAt: now,
    };

    await setAllStockItems((prev) => {
      if (editingId) return prev.map((item) => (item.id === editingId ? next : item));
      return [next, ...prev];
    });
    resetForm();
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Stok kaydı silinsin mi?")) return;
    await setAllStockItems((prev) => prev.filter((item) => item.id !== id));
  }

  function draftFromAnimal(animalId: number) {
    const animal = animals.find((item) => item.id === animalId);
    if (!animal) return;
    setForm((prev) => ({
      ...prev,
      kupeNo: animal.kupeNo,
      siparisNo: String(animal.siraNo ?? ""),
      tur: animal.tur,
      kg: String(animal.kg ?? ""),
      kategori: "Canlı Hayvan",
      satisGeliri: String(animal.hisseler.reduce((sum, share) => sum + share.tutar + (share.kesimUcreti ?? 0) + (share.parcalamaUcreti ?? 0) + (share.tasimaUcreti ?? 0) - (share.indirim ?? 0), 0)),
      durum: animal.tamamlandi ? "Kesildi" : animal.hisseler.length > 0 ? "Satışta" : "Stokta",
    }));
  }

  async function importBulk() {
    const rows = bulkText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (rows.length === 0) return;

    const now = new Date().toISOString();
    const nextRows: StockItem[] = rows.map((line) => {
      const [kupeNo, kg, alisTutari, tedarikci, not] = line.split(";").map((x) => x?.trim() ?? "");
      return {
        id: crypto.randomUUID(),
        tarih: today(),
        kupeNo,
        tur: "Büyükbaş",
        kategori: "Canlı Hayvan",
        kg: num(kg),
        alisTutari: num(alisTutari),
        tedarikci,
        not,
        durum: "Stokta",
        createdAt: now,
        updatedAt: now,
      };
    }).filter((item) => item.kupeNo);

    await setAllStockItems((prev) => [...nextRows, ...prev]);
    setBulkText("");
  }

  function exportCurrent() {
    const columns: ExportColumn<(typeof filtered)[number]>[] = [
      { key: "tarih", label: "Tarih" },
      { key: "kupeNo", label: "Küpe / Stok Kodu" },
      { key: "siparisNo", label: "Sipariş No" },
      { key: "tur", label: "Tür" },
      { key: "kategori", label: "Kategori" },
      { key: "tedarikci", label: "Tedarikçi" },
      { key: "kg", label: "Canlı KG" },
      { key: "kesimKg", label: "Kesim KG" },
      { key: "cost", label: "Toplam Maliyet" },
      { key: "revenue", label: "Satış/Gelir" },
      { key: "paid", label: "Tahsilat" },
      { key: "profit", label: "Kar/Zarar" },
      { key: "durum", label: "Durum" },
      { key: "not", label: "Not" },
    ];
    exportRowsToCsv(`stok-maliyet-${new Date().toISOString().slice(0, 10)}`, filtered, columns);
  }

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-7xl mx-auto px-3 sm:px-5 py-4 space-y-4">
        <div className="rounded-farm border border-farm-border bg-white px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-heading text-xl font-bold text-farm-brown">Stok / Mal Kabul / Maliyet</h1>
              <p className="mt-1 text-sm text-farm-muted">
                Stok kendi kayıtlarını tutar; Hayvanlar, Satışlar ve Ödemeler verisini sadece okur, değiştirmez.
              </p>
            </div>
            <button type="button" onClick={exportCurrent} className="inline-flex items-center gap-2 rounded-full border border-farm-border bg-white px-3 py-2 text-xs font-semibold text-farm-brown hover:border-farm-earth/40">
              <i className="ri-file-excel-2-line text-base" />
              Excel Çıktısı
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-2">
          <Stat label="Stok Kayıt" value={totals.count} />
          <Stat label="Canlı KG" value={totals.kg.toLocaleString("tr-TR")} />
          <Stat label="Maliyet" value={fmt(totals.cost)} tone="earth" />
          <Stat label="Satış/Gelir" value={fmt(totals.revenue)} tone="green" />
          <Stat label="Tahsilat" value={fmt(totals.paid)} tone="gold" />
          <Stat label="Kar/Zarar" value={fmt(totals.profit)} tone={totals.profit >= 0 ? "green" : "red"} />
        </div>

        <div className="grid gap-4 lg:grid-cols-[390px_minmax(0,1fr)]">
          <div className="space-y-4">
            <div className="rounded-farm border border-farm-border bg-white p-4">
              <h2 className="font-heading text-base font-bold text-farm-brown">{editingId ? "Stok Düzenle" : "Mal Kabul / Stok Ekle"}</h2>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                <div><Label>Tarih</Label><input type="date" value={form.tarih} onChange={(e) => setForm((p) => ({ ...p, tarih: e.target.value }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" /></div>
                <div><Label>Küpe / Stok Kodu</Label><input value={form.kupeNo} onChange={(e) => setForm((p) => ({ ...p, kupeNo: e.target.value }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" /></div>
                <div><Label>Sipariş / Belge No</Label><input value={form.siparisNo} onChange={(e) => setForm((p) => ({ ...p, siparisNo: e.target.value }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" /></div>
                <div><Label>Tedarikçi</Label><input value={form.tedarikci} onChange={(e) => setForm((p) => ({ ...p, tedarikci: e.target.value }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" /></div>
                <div><Label>Tür</Label><select value={form.tur} onChange={(e) => setForm((p) => ({ ...p, tur: e.target.value as StockItem["tur"] }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none">{["Büyükbaş", "Küçükbaş", "Karkas", "Parçalanmış Ürün", "Diğer"].map((x) => <option key={x}>{x}</option>)}</select></div>
                <div><Label>Durum</Label><select value={form.durum} onChange={(e) => setForm((p) => ({ ...p, durum: e.target.value as StockItem["durum"] }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none">{["Stokta", "Satışta", "Rezerve", "Kesildi", "Parçalandı", "Satıldı", "Fire / Zayi", "Pasif"].map((x) => <option key={x}>{x}</option>)}</select></div>
                <div><Label>Canlı KG</Label><input value={form.kg} onChange={(e) => setForm((p) => ({ ...p, kg: e.target.value.replace(/[^\d.,]/g, "") }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" /></div>
                <div><Label>Kesim Sonrası KG</Label><input value={form.kesimKg} onChange={(e) => setForm((p) => ({ ...p, kesimKg: e.target.value.replace(/[^\d.,]/g, "") }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" /></div>
                <div><Label>Alış Tutarı</Label><input value={form.alisTutari} onChange={(e) => setForm((p) => ({ ...p, alisTutari: e.target.value.replace(/[^\d.,]/g, "") }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" /></div>
                <div><Label>Nakliye</Label><input value={form.nakliyeMaliyeti} onChange={(e) => setForm((p) => ({ ...p, nakliyeMaliyeti: e.target.value.replace(/[^\d.,]/g, "") }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" /></div>
                <div><Label>Kesim Maliyeti</Label><input value={form.kesimMaliyeti} onChange={(e) => setForm((p) => ({ ...p, kesimMaliyeti: e.target.value.replace(/[^\d.,]/g, "") }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" /></div>
                <div><Label>Parçalama Maliyeti</Label><input value={form.parcalamaMaliyeti} onChange={(e) => setForm((p) => ({ ...p, parcalamaMaliyeti: e.target.value.replace(/[^\d.,]/g, "") }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" /></div>
                <div><Label>Diğer Maliyet</Label><input value={form.maliyetTutari} onChange={(e) => setForm((p) => ({ ...p, maliyetTutari: e.target.value.replace(/[^\d.,]/g, "") }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" /></div>
                <div><Label>Harici Satış/Gelir</Label><input value={form.satisGeliri} onChange={(e) => setForm((p) => ({ ...p, satisGeliri: e.target.value.replace(/[^\d.,]/g, "") }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" /></div>
                <div className="sm:col-span-2 lg:col-span-1"><Label>Not</Label><textarea value={form.not} onChange={(e) => setForm((p) => ({ ...p, not: e.target.value }))} rows={3} className="w-full rounded-xl border border-farm-border bg-farm-cream px-3 py-2 text-sm outline-none" /></div>
                <div className="flex gap-2 sm:col-span-2 lg:col-span-1">
                  <button type="button" onClick={handleSave} className="h-10 flex-1 rounded-xl bg-farm-earth text-sm font-semibold text-white">{editingId ? "Güncelle" : "Stoka Ekle"}</button>
                  {editingId && <button type="button" onClick={resetForm} className="h-10 rounded-xl border border-farm-border bg-white px-3 text-xs font-semibold text-farm-muted">Vazgeç</button>}
                </div>
              </div>
            </div>

            <div className="rounded-farm border border-farm-border bg-white p-4">
              <h2 className="font-heading text-base font-bold text-farm-brown">Toplu Giriş</h2>
              <p className="mt-1 text-xs text-farm-muted">Her satır: Küpe; KG; Alış Tutarı; Tedarikçi; Not</p>
              <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={5} className="mt-3 w-full rounded-xl border border-farm-border bg-farm-cream px-3 py-2 text-xs outline-none" placeholder={"TR-001; 450; 120000; Ahmet Çiftliği; Not\nTR-002; 420; 115000; Ahmet Çiftliği; Not"} />
              <button type="button" onClick={importBulk} className="mt-2 h-9 w-full rounded-xl border border-farm-earth bg-white text-xs font-semibold text-farm-earth hover:bg-farm-earth hover:text-white">Toplu Ekle</button>
            </div>
          </div>

          <div className="rounded-farm border border-farm-border bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="font-heading text-base font-bold text-farm-brown">Stok Listesi ve Maliyet Analizi</h2>
                <p className="text-xs text-farm-muted">Küpe eşleşirse mevcut hayvan/satış/ödeme verileri sağdaki analizde okunur.</p>
              </div>
              <div className="flex gap-2">
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Stokta ara" className="h-9 rounded-xl border border-farm-border bg-farm-cream px-3 text-xs outline-none" />
                <select value={durumFilter} onChange={(e) => setDurumFilter(e.target.value)} className="h-9 rounded-xl border border-farm-border bg-farm-cream px-2 text-xs outline-none">
                  <option>Tümü</option>
                  {["Stokta", "Satışta", "Rezerve", "Kesildi", "Parçalandı", "Satıldı", "Fire / Zayi", "Pasif"].map((x) => <option key={x}>{x}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-3 overflow-hidden rounded-2xl border border-farm-border">
              <div className="hidden lg:grid grid-cols-[92px_100px_95px_95px_90px_110px_110px_100px_105px_80px] bg-farm-cream px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-farm-muted">
                <div>Tarih</div><div>Küpe</div><div>Tür</div><div>Durum</div><div>KG</div><div className="text-right">Maliyet</div><div className="text-right">Gelir</div><div className="text-right">Tahsilat</div><div className="text-right">Kar/Zarar</div><div></div>
              </div>
              {filtered.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-farm-muted">Stok kaydı yok.</div>
              ) : filtered.map((item) => (
                <div key={item.id} className="border-t border-farm-border px-3 py-2 text-xs lg:grid lg:grid-cols-[92px_100px_95px_95px_90px_110px_110px_100px_105px_80px] lg:items-center">
                  <div>{item.tarih}</div>
                  <div className="font-semibold text-farm-brown">{item.kupeNo}<div className="text-[10px] text-farm-muted">{item.siparisNo || item.tedarikci || "—"}</div></div>
                  <div>{item.tur}</div>
                  <div>{item.durum}</div>
                  <div>{item.kg || "—"}</div>
                  <div className="text-right">{fmt(item.cost)}</div>
                  <div className="text-right text-farm-green">{fmt(item.revenue)}</div>
                  <div className="text-right text-farm-gold">{fmt(item.paid)}</div>
                  <div className={`text-right font-bold ${item.profit >= 0 ? "text-farm-green" : "text-red-600"}`}>{fmt(item.profit)}</div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => editItem(item)} className="text-farm-earth">Düzenle</button>
                    <button type="button" onClick={() => handleDelete(item.id)} className="text-red-600">Sil</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-farm border border-dashed border-farm-border bg-farm-cream p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold text-farm-brown">Mevcut Hayvanlardan Taslak Al</p>
                  <p className="text-[11px] text-farm-muted">Sadece stok formunu doldurur; Hayvanlar sayfasına yazmaz.</p>
                </div>
                <select onChange={(e) => draftFromAnimal(Number(e.target.value))} value="" className="h-9 rounded-xl border border-farm-border bg-white px-2 text-xs outline-none">
                  <option value="">Hayvan seç</option>
                  {animals.map((animal) => <option key={animal.id} value={animal.id}>{animal.kupeNo} / Sipariş {animal.siraNo}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
